import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Dashboard
import Dashboard from "./pages/dashboard/Dashboard";

// Authentication
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";

// Resources
import PromptList from "./pages/prompts/PromptList";
import PromptDetail from "./pages/prompts/PromptDetail";
import ProviderList from "./pages/providers/ProviderList";
import ProviderDetail from "./pages/providers/ProviderDetail";
import DatasetList from "./pages/datasets/DatasetList";
import DatasetDetail from "./pages/datasets/DatasetDetail";

// Evaluations
import EvaluationList from "./pages/evaluations/EvaluationList";
import EvaluationDetail from "./pages/evaluations/EvaluationDetail";

// Other
import NotFound from "./pages/NotFound";
import { useAuth0 } from "@auth0/auth0-react";

interface AuthGuardProps {
  children: React.ReactNode;
}

// Auth guard - placeholder for now
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Use loginWithRedirect when user is not authenticated
      // instead of just redirecting to login page
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname,
        },
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  // Show loading indicator while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Only render children when authenticated
  return isAuthenticated ? <>{children}</> : null;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <AuthGuard>
            <Dashboard />
          </AuthGuard>
        }
      />

      {/* Profile */}
      <Route
        path="/profile"
        element={
          <AuthGuard>
            <Profile />
          </AuthGuard>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <AuthGuard>
            <Settings />
          </AuthGuard>
        }
      />

      {/* Prompts */}
      <Route
        path="/prompts"
        element={
          <AuthGuard>
            <PromptList />
          </AuthGuard>
        }
      />

      <Route
        path="/prompts/:id"
        element={
          <AuthGuard>
            <PromptDetail />
          </AuthGuard>
        }
      />

      {/* Prompt Tests */}
      <Route
        path="/datasets"
        element={
          <AuthGuard>
            <DatasetList />
          </AuthGuard>
        }
      />

      <Route
        path="/datasets/:id"
        element={
          <AuthGuard>
            <DatasetDetail />
          </AuthGuard>
        }
      />
      <Route
        path="/datasets/:id/edit"
        element={
          <AuthGuard>
            <DatasetDetail />
          </AuthGuard>
        }
      />

      {/* Providers */}
      <Route
        path="/providers"
        element={
          <AuthGuard>
            <ProviderList />
          </AuthGuard>
        }
      />

      <Route
        path="/providers/:id"
        element={
          <AuthGuard>
            <ProviderDetail />
          </AuthGuard>
        }
      />

      {/* Evaluations */}
      <Route
        path="/evaluations"
        element={
          <AuthGuard>
            <EvaluationList />
          </AuthGuard>
        }
      />

      <Route
        path="/evaluations/:id"
        element={
          <AuthGuard>
            <EvaluationDetail />
          </AuthGuard>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
